/**
 * PGSS-5715: a login switched off while it is signed in.
 *
 * The server checks `users.is_active` on every request and answers a disabled user with a 400 whose
 * message starts "User has been disabled" (ApiInterceptor -> AccessException -> GlobalExceptionHandler).
 * That message names the account, so it is never shown; the user is signed out and told plainly.
 *
 * Sign-out reloads the app, which would drop an on-screen toast, so the notice is parked in
 * sessionStorage and shown once by AuthContext after the reload.
 */
import { getApiErrorPayload } from 'app/utils/apiError';

export const DISABLED_LOGIN_NOTICE = 'Your access has been turned off. Please contact PSG.';
const NOTICE_KEY = 'psg.signOutNotice';
const DISABLED_PREFIX = 'User has been disabled';

export function isDisabledLoginError(error) {
  const status = error?.response?.status ?? error?.status;
  const payload = getApiErrorPayload(error);
  const message = typeof payload === 'string' ? payload : payload?.message;
  return status === 400 && typeof message === 'string' && message.trim().startsWith(DISABLED_PREFIX);
}

export function setSignOutNotice(message) {
  try {
    window.sessionStorage.setItem(NOTICE_KEY, message);
  } catch (err) {
    /* storage blocked: the on-page notice still shows until the reload */
  }
}

/** Returns the parked notice once, then forgets it. */
export function takeSignOutNotice() {
  try {
    const message = window.sessionStorage.getItem(NOTICE_KEY);
    if (message) window.sessionStorage.removeItem(NOTICE_KEY);
    return message || null;
  } catch (err) {
    return null;
  }
}

/** Park the friendly notice, then sign out through the app's standard path. */
export function signOutDisabledLogin(jwtService) {
  setSignOutNotice(DISABLED_LOGIN_NOTICE);
  if (jwtService && typeof jwtService.logout === 'function') {
    jwtService.logout();
  }
}
